<template>
  <div class="base-valid-number">
    <el-popover
        :popper-class="msgPopperClass"
        trigger="focus">
      <div class="is-error-msg">
        {{ $errorMsg }}
      </div>
      <el-input
          ref="input"
          slot="reference"
          v-validate="validationRule"
          :data-vv-name="uid"
          :data-vv-scope="validationScope"
          :class="{'is-error': $errorMsg }"
          :value="inputValue"
          :name="name"
          :disabled="disabled"
          :size="size"
          :label="label"
          :placeholder="placeholder"
          type="number"
          @input="onInput($event)"
          @change="onChange($event)"
          @focus="onFocus($event)"
          @blur="onBlur($event)"
          @keydown.native="inputLimit" />
    </el-popover>
    <div v-show="!popoverErrorMsg && $errorMsg"
        class="is-error-msg">
      {{ $errorMsg }}
    </div>
  </div>
</template>

<style scoped>
.base-valid-number ::v-deep input[type=number]::-webkit-inner-spin-button,
.base-valid-number ::v-deep input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.base-valid-number ::v-deep input[type="number"] {
  -moz-appearance: textfield;
}
</style>

<script>
import _ from 'lodash'
import ValidCore from '@/components/atoms/BaseValidCore'
import math from '@/utils/math'

export default {
  name: 'BaseValidNumber',
  mixins: [ValidCore],
  props: {
    disabled: { type: Boolean, default: false },
    size: {
      type: String,
      default: undefined,
      validator: function (value) {
        return ['medium', 'small', 'mini'].indexOf(value) !== -1
      }
    },
    label: { type: String, default: undefined },
    placeholder: { type: String, default: undefined },
    precision: {
      type: Number,
      default: 0,
      validator: function validator (val) {
        return val >= 0 && val === parseInt(val, 10)
      }
    }
  },
  data () {
    return {
      whenFocused: null
    }
  },
  computed: {
    uid () {
      return this._uid.toString()
    },
    validationRule () {
      return `decimal${this.precision > 0 ? ':' + this.precision : ''}`
    },
    validationScope () {
      return _.get(this.$attrs, 'data-vv-scope', 'BaseValidNumber')
    },
    $errorMsg () {
      if (this.errors.has(this.uid, this.validationScope)) {
        return this.precision > 0
          ? '整数または小数点を含んだ数値を入力しください'
          : '整数の数値を入力しください'
      }
      return this.errorMsg
    }
  },
  created () {
  },
  mounted () {
    this.$el.addEventListener('paste', this.onPaste)

    this.inputValue = this.toHalfWidthNumber(this.inputValue)
  },
  beforeDestroy () {
    this.$el.removeEventListener('paste', this.onPaste)
  },
  methods: {
    onInput (event) {
      this.inputValue = event
    },
    onChange (event) {
      let val = this.toHalfWidthNumber(event)
      this.updateInputValue(val)
      if (event !== val.toString()) {
        this.$validator.validate(`${this.validationScope}.${this.uid}`)
      }
    },
    onFocus (event) {
      this.whenFocused = _.cloneDeep(this.inputValue)
      this.$emit('focus', event)
    },
    onBlur (event) {
      this.$emit('blur', event, this.whenFocused, this.inputValue)
    },
    onPaste (event) {
      let text = event.clipboardData.getData('text')
      if (!text) {
        return
      }
      event.preventDefault()
      event.stopPropagation()

      this.updateInputValue(this.toHalfWidthNumber(text))
      setTimeout(() => this.$validator.validate(`${this.validationScope}.${this.uid}`), 100)
    },
    /**
     * valを半角数字の数値に変換し、カンマを削除して返却する.
     * valに数字以外が含まれている場合は、0 を返却する.
     */
    toHalfWidthNumber (val) {
      if (typeof val === 'string' && val.match(/^(?:(?:[-－][０-９\d])|[０-９\d])(?:[.,．，]?[０-９\d]+)*$/) != null) {
        let ret = val
          .replace(/[０-９－．，]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xFEE0))
          .replace(/,/g, '')
        ret = math.floor(
          Number(ret),
          this.precision)
        return Number.isNaN(ret) ? val : ret
      } else if (val == null || val === undefined) {
        return val
      } else {
        return isNaN(Number(val)) ? 0 : val
      }
    },
    // 親からインプット要素をフォーカスするために使われる
    focus: function () {
      this.$refs.input.focus()
    },
    inputLimit (e) {
      let key = e.key
      // 'e'と'E'を禁止
      if (key === 'e' || key === 'E') {
        e.returnValue = false
        return false
      }
      return true
    }
  }
}
</script>
